import { donationTypeLabels, donationTypes } from '../constants';
import { shouldObscureContacts } from './contacts';
import { getPageUrl } from './pages';
import { UNLAYER_FILE_MANAGER_FEATURES } from './images';

const getCustomSubstitutionTagsFromString = (string) => string.match(/{{(.*?)}}/g);

export const getCustomSubstitutionTagDefaultValues = (string) => {
  const tags = getCustomSubstitutionTagsFromString(string) || [];

  if (tags.length === 0) return {};

  return tags.reduce((prev, tag) => {
    const [tagName, defaultValue] = tag
      .replace(/{{|}}/g, '')
      .split('|')
      .map((item) => item.trim());
    // eslint-disable-next-line no-param-reassign
    if (!prev[tagName]) prev[tagName] = defaultValue ?? '';
    return prev;
  }, {});
};

export const replaceCustomTagsWithSendgridTags = (string) => string.replace(/\s?\|(.*?)}}/g, '}}');

export const substituteTagWithValue = (string, tagName, value) =>
  string.replace(new RegExp(`{{${tagName}}}`, 'g'), value);

export { injectTemplateHtmlSections, injectTemplateDesignSections } from 'common/utils';

export const replaceTemplateHtmlDonateButtonStyles = (html, { backgroundColor, textColor }) => {
  const templateDocument = new DOMParser().parseFromString(html, 'text/html');
  const donateButtons = templateDocument.querySelectorAll('.v-button');
  donateButtons.forEach((button) => {
    button.style.backgroundColor = backgroundColor;
    button.style.color = textColor;
  });
  return templateDocument.documentElement.innerHTML;
};

export const replaceTemplateDesignDonateButtonStyles = (design, { backgroundColor, textColor }) => {
  const templateDesign = JSON.parse(design);
  templateDesign.body.rows.forEach((row) => {
    row.columns.forEach((column) => {
      column.contents.forEach((content) => {
        if (content.type === 'button') {
          content.values.buttonColors.backgroundColor = backgroundColor;
          content.values.buttonColors.color = textColor;
        }
      });
    });
  });
  return JSON.stringify(templateDesign);
};

export const getLinkTypes = ({
  donationPageUrl,
  pageList,
  account,
  designations = [],
  embeddedDonationPages = [],
  campaignId = '',
}) => {
  const [pageUrl, queryParams] = donationPageUrl.split('/?');

  const slugOptions = pageList.map((page) => ({
    value: page.slug === 'default' ? '/' : `/${page.slug}`,
    label: page.name,
  }));

  const embeddedPageOptions = embeddedDonationPages.map((page) => {
    const url = new URL(page.url);
    if (campaignId) url.searchParams.set('campaignId', campaignId);
    return {
      value: url.toString(),
      label: page.name,
    };
  });

  return [
    {
      name: 'web',
      enabled: !shouldObscureContacts(account),
    },
    {
      name: 'phone',
      enabled: false,
    },
    {
      name: 'email',
      enabled: false,
    },
    {
      name: 'sms',
      enabled: false,
    },
    {
      name: 'static_donation_page_link',
      label: 'Go To Donation Page',
      attrs: {
        href: `${pageUrl}{{slug}}?${queryParams}&amount={{amount}}&type={{type}}&designationId={{designationId}}`,
        target: '_blank',
      },
      fields: [
        {
          name: 'slug',
          label: 'Donation Page',
          defaultValue: '/',
          inputType: null,
          placeholderText: null,
          options: slugOptions,
        },
        {
          name: 'amount',
          label: 'Amount',
          defaultValue: account.emailPreferences.donateButton.amount,
          inputType: 'number',
          placeholderText: null,
        },
        {
          name: 'type',
          label: 'Type',
          defaultValue: account.emailPreferences.donateButton.type,
          inputType: null,
          placeholderText: null,
          options: [
            { value: donationTypes.recurring, label: donationTypeLabels.recurring },
            { value: donationTypes.oneTime, label: donationTypeLabels[donationTypes.oneTime] },
          ],
        },
        {
          name: 'designationId',
          label: 'Designation',
          defaultValue: '',
          inputType: null,
          placeholderText: null,
          options: [
            { value: '', label: '' },
            ...designations
              .map((designation) => ({ value: designation.designationId, label: designation.name }))
              .sort((a, b) => a.label.localeCompare(b.label)),
          ],
        },
      ],
    },
    ...(embeddedPageOptions.length > 0
      ? [
          {
            name: 'embedded_donation_page_link',
            label: 'Embedded Donation Page',
            attrs: {
              href: '{{url}}&amount={{amount}}&type={{type}}&designationId={{designationId}}',
              target: '_blank',
            },
            fields: [
              {
                name: 'url',
                label: 'Embedded Donation Page',
                defaultValue: embeddedPageOptions[0]?.value || '',
                inputType: null,
                placeholderText: null,
                options: embeddedPageOptions,
              },
              {
                name: 'amount',
                label: 'Amount',
                defaultValue: account.emailPreferences.donateButton.amount,
                inputType: 'number',
                placeholderText: null,
              },
              {
                name: 'type',
                label: 'Type',
                defaultValue: account.emailPreferences.donateButton.type,
                inputType: null,
                placeholderText: null,
                options: [
                  { value: donationTypes.recurring, label: donationTypeLabels.recurring },
                  { value: donationTypes.oneTime, label: donationTypeLabels[donationTypes.oneTime] },
                ],
              },
              {
                name: 'designationId',
                label: 'Designation',
                defaultValue: '',
                inputType: null,
                placeholderText: null,
                options: [
                  { value: '', label: '' },
                  ...designations
                    .map((designation) => ({ value: designation.designationId, label: designation.name }))
                    .sort((a, b) => a.label.localeCompare(b.label)),
                ],
              },
            ],
          },
        ]
      : []),
  ];
};

export const getEditorOptions = ({ account, campaignDetails }) => {
  const embeddedDonationPages = account.embeddedDonationPages || [];
  const defaultEmbeddedPageUrl = account.defaultEmbeddedDonationPageUrl || '';
  const defaultEmbeddedPage = embeddedDonationPages.find((p) => p.url === defaultEmbeddedPageUrl);
  const campaignId = campaignDetails.campaignId;

  let defaultHref;
  if (defaultEmbeddedPage) {
    const url = new URL(defaultEmbeddedPage.url);
    if (campaignId) url.searchParams.set('campaignId', campaignId);
    url.searchParams.set('amount', account.emailPreferences.donateButton.amount);
    url.searchParams.set('type', account.emailPreferences.donateButton.type);
    defaultHref = {
      name: 'embedded_donation_page_link',
      attrs: {
        href: url.toString(),
        target: '_blank',
      },
    };
  } else {
    defaultHref = {
      name: 'static_donation_page_link',
      attrs: {
        href: `${getPageUrl({
          account,
          params: {
            campaignId,
            amount: account.emailPreferences.donateButton.amount,
            type: account.emailPreferences.donateButton.type,
          },
        })}`,
        target: '_blank',
      },
    };
  }

  return {
    projectId: 116972,
    // End-user identification: scopes the editor's saved blocks and file-manager
    // uploads to the current tenant instead of sharing them project-wide.
    user: {
      id: account.tenantId,
    },
    features: {
      ai: false,
      ...UNLAYER_FILE_MANAGER_FEATURES,
    },
    tools: {
      button: {
        properties: {
          buttonColors: {
            value: {
              color: account.emailPreferences.donateButton.textColor,
              backgroundColor: account.emailPreferences.donateButton.backgroundColor,
            },
          },
          borderRadius: {
            value: '0px',
          },
          text: {
            value: account.emailPreferences.donateButton.buttonText,
          },
          href: {
            value: defaultHref,
          },
        },
      },
    },
    designTagsConfig: {
      // Disables default design tags delimiter '[[]]' which is used for suggested drafts custom prompt tags.
      delimiter: ['|<|', '|>|'],
    },
  };
};
